import { useEffect, useState, type FunctionComponent } from "react";
import ClockIcon from "../../../assets/svgs/common/clock.svg";
import { formatDate, handleHttpError } from "../../../utils/utils";
import DownloadIcon from "../../../assets/svgs/common/downlaod.svg";
import MediaCarousel from "../../../components/carousel/carousel.components";
import { CarouselItem } from "../../../components/ui/carousel";
import { useGetCampaignBriefCommonHttp } from "../../../hooks/http/common/utils/campaign-brief/get-campaign-brief.common.http";
import type { ICampaign } from "../../../types/models/campaign.models.types";
import { useToast } from "../../../hooks/use-toast";
import Spinner from "../../../components/spinner.components";
import { Link, useParams } from "react-router-dom";
import millify from "millify";
import SwapIcon from "../../../assets/svgs/common/swap.svg";
import AddImageIcon from "../../../assets/svgs/common/add-image.svg";

export const CampaignBriefCommonPartial: FunctionComponent = () => {
	const { execute: getCampaignBriefHttp, fetching: gettingBrief } =
		useGetCampaignBriefCommonHttp();
	const [campaignBrief, setCampaignBrief] = useState<ICampaign>();

	const { toast } = useToast();

	const { campaignId } = useParams();

	async function getCampaignBrief() {
		if (!campaignId) {
			return;
		}
		const response = await getCampaignBriefHttp({
			body: null,
			queryParams: {
				campaignId: campaignId,
			},
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setCampaignBrief(response.data.campaign);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		getCampaignBrief();
	}, []);

	const startDate = campaignBrief?.startDate;
	const endDate = campaignBrief?.endDate;

	const initialCampaignDetails = [
		{
			title: "Campaign name",
			value: campaignBrief?.name,
		},
		{
			title: "Post type",
			value: campaignBrief?.socialPostType,
		},
		{
			title: "Creator type",
			value: campaignBrief?.socialCreatorType,
		},
		{
			title: "Start date",
			value: startDate ? formatDate(new Date(startDate).toISOString()) : "",
		},
		{
			title: "End date",
			value: endDate ? formatDate(new Date(endDate).toISOString()) : "",
		},
		{
			title: "Budget",
			value: `₹ ${millify(campaignBrief?.budget ?? 0)}`,
		},
	];

	if (!campaignBrief) {
		return null;
	}

	if (gettingBrief) {
		return (
			<div className="flex justify-center items-center w-full h-screen">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-24 px-20 py-16 w-full">
			<div className="flex flex-col gap-10">
				<h1 className="font-bold text-2xl">Campaign Brief</h1>
				<div className="flex justify-items-start items-center gap-10">
					{campaignBrief.coverImage?.url ? (
						<img
							className="rounded-2xl w-full max-w-md h-64 object-cover"
							src={campaignBrief?.coverImage?.url}
							alt=""
						/>
					) : (
						<div className="flex justify-center items-center border border-border rounded-lg w-full max-w-md h-64">
							<AddImageIcon />
						</div>
					)}

					<div className="flex flex-col gap-7">
						<div className="flex items-center gap-1 px-4 py-2 border border-border rounded-full w-fit">
							<ClockIcon />
							<p className="font-semibold text-base">
								{startDate && endDate
									? `${formatDate(new Date(startDate).toISOString())} - ${formatDate(new Date(endDate).toISOString())}`
									: ""}
							</p>
						</div>
						<h1 className="font-semibold text-3xl text-primary">
							{campaignBrief?.name}
						</h1>
					</div>
				</div>
			</div>
			<div className="gap-x-10 gap-y-16 grid grid-cols-3 text-primary">
				{initialCampaignDetails.map((detail, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
					<div key={index} className="flex flex-col gap-4">
						<p className="font-semibold text-base text-secondary">
							{detail.title}
						</p>
						<div className="border-t border-border w-full" />
						<p className="pt- font-semibold text-lg text-primary">
							{detail.value}
						</p>
					</div>
				))}
			</div>
			<div className="flex flex-col gap-3">
				<h1 className="font-bold text-base text-secondary">Milestones</h1>
				<div className="flex flex-col gap-4">
					{campaignBrief.milestones?.map((milestone, index) => (
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
							key={index}
							className="flex justify-between items-center px-14 py-6 border-t border-border"
						>
							<p className="flex justify-between items-center gap-1 w-28 font-semibold text-base text-secondary">
								for
								<span className="text-right w-full text-2xl text-primary">
									{millify(milestone.count)}
								</span>
								<span className="font-base">views</span>
							</p>
							<SwapIcon />
							<p className="flex items-center gap-4 font-semibold text-base text-primary">
								<span className="font-base text-secondary">payout</span>
								<span className="w-24 text-2xl">
									₹ {millify(milestone.payout)}
								</span>
							</p>
						</div>
					))}
				</div>
			</div>
			<div className="flex items-start gap-16 w-full">
				<div className="flex flex-col flex-1 gap-3">
					<h1 className="font-bold text-base text-secondary">
						Campaign assets
					</h1>
					<div className="flex flex-col gap-4 pt-4 border-t border-border w-full">
						{campaignBrief?.assets.map((asset) => (
							<div
								// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
								key={asset.id}
								className="flex justify-between items-center gap-4 px-6 py-6 border border-border rounded-xl"
							>
								<p className="font-semibold text-lg text-primary">
									{asset.name}
								</p>
								<Link to={asset.url} target="_blank" className="cursor-pointer">
									<DownloadIcon />
								</Link>
							</div>
						))}
					</div>
				</div>
				<div className="flex flex-col flex-1 gap-3">
					<h1 className="font-bold text-base text-secondary">Sample videos</h1>
					<div className="px-36 pt-4 border-t border-border w-full">
						<MediaCarousel
							className="flex flex-col gap-8"
							count={campaignBrief?.sampleVideos.length ?? 0}
						>
							{campaignBrief?.sampleVideos.map((video) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
								<CarouselItem key={video.id}>
									<div className="relative flex justify-between items-center gap-4 border border-border rounded-xl">
										{/* biome-ignore lint/a11y/useMediaCaption: <explanation> */}
										<video
											src={video.url}
											controls
											className="rounded-xl w-full h-[530px] aspect-video object-cover"
										/>
										<div
											className={
												"bottom-4 left-4 absolute w-64 font-semibold text-nowrap text-sm truncate overflow-hidden "
											}
										>
											{video.name}
										</div>
									</div>
								</CarouselItem>
							))}
						</MediaCarousel>
					</div>
				</div>
			</div>
			<div className="flex items-start gap-16 w-full">
				<div className="flex flex-col flex-1 gap-3">
					<h1 className="font-bold text-base text-secondary">
						Mandatory hashtags
					</h1>
					<div className="flex pt-4 border-t border-border w-full font-semibold">
						<p className="flex gap-2 font-base text-wrap tracking-wider">
							{campaignBrief?.hashtags}
						</p>
					</div>
				</div>
				<div className="flex flex-col flex-1 gap-3">
					<h1 className="font-bold text-base text-secondary">
						Mandatory mentions
					</h1>
					<div className="flex pt-4 border-t border-border w-full font-semibold">
						<p className="flex gap-2 font-base text-wrap tracking-wider">
							{campaignBrief?.mentions}
						</p>
					</div>
				</div>
			</div>
			<div className="flex flex-col flex-1 gap-3">
				<h1 className="font-bold text-base text-secondary">About campaign </h1>
				<div className="flex pt-4 border-t border-border w-full font-semibold">
					<p className="flex gap-2 font-base text-wrap tracking-wider">
						{campaignBrief?.aboutCampaign}
					</p>
				</div>
			</div>
			<div className="flex flex-col flex-1 gap-3">
				<h1 className="font-bold text-base text-secondary">Do’s and Dont’s </h1>
				<div className="flex flex-col gap-4 pt-4 border-t border-border w-full font-semibold">
					<p className="flex gap-2 font-base text-wrap tracking-wider">
						{campaignBrief?.do}
					</p>
					<p className="flex gap-2 font-base text-wrap tracking-wider">
						{campaignBrief?.dont}
					</p>
				</div>
			</div>
			<div className="flex flex-col flex-1 gap-3">
				<h1 className="font-bold text-base text-secondary">About brand</h1>
				<div className="flex pt-4 border-t border-border w-full font-semibold">
					<p className="flex gap-2 font-base text-wrap tracking-wider">
						{campaignBrief?.aboutBrand}
					</p>
				</div>
			</div>
			<div className="flex flex-col flex-1 gap-3">
				<h1 className="font-bold text-base text-secondary">
					About cash payment process
				</h1>
				<div className="flex pt-4 border-t border-border w-full font-semibold">
					<p className="flex gap-2 font-base text-wrap tracking-wider">
						{campaignBrief?.aboutPayment}
					</p>
				</div>
			</div>
		</div>
	);
};
